/**
 * CR306 Appendix 2: V2 Debug Inspector Panel
 * CR307: Enhanced with registry alignment, labels, details surface, unknown toggle warnings
 * CR310: Added label source tracing with per-key source display
 * CR316: Added render status tracking per block (rendered/toggle_off/no_data/error)
 * 
 * Displays real-time V2 spec state for debugging:
 * - deal_type_key, variant_key, renderer (V2 forced)
 * - Effective styleOverrides (showMerchantLogo, showMerchantName, etc.)
 * - Effective surfaceOverrides block toggles (primary + details)
 * - Render status per block with reasons
 * - Effective labels with source per key
 * - Active registry block IDs per surface
 * - Unknown toggle IDs list (registry mismatch warnings)
 * - Source tracing: variant/template/overrides/draftOverrides
 */

import { useState, useMemo } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Bug, ChevronDown, ChevronRight, Layers, Palette, Eye, EyeOff, Code2, AlertTriangle, Tag, LayoutList, Check, X, HelpCircle, Shield } from "lucide-react";
import type { ResolvedDealCardSpecV2 } from "@/lib/deal-cards/specV2/types";
import { 
  REGISTRY_PRIMARY_BLOCK_IDS, 
  REGISTRY_DETAILS_BLOCK_IDS,
  getUnknownToggleIds,
} from "@/lib/deal-cards/templateBridge";
import {
  resolveEffectiveLabels,
  getLabelSourceDisplayName,
  getUniversalPrimaryBlockIds,
  getUniversalDetailsBlockIds,
  getMissingUniversalBlocks,
  UNIVERSAL_PRIMARY_BLOCK_IDS,
  UNIVERSAL_DETAILS_BLOCK_IDS,
  analyzeTemplateForThemeKeys,
  type LabelSource,
  type RenderStatusCollection,
  getRenderReasonDisplayText,
  getRenderReasonBadgeClass,
} from "@/lib/deal-cards/specV2";
import { cn } from "@/lib/utils";

interface V2DebugInspectorProps {
  /** The resolved V2 spec (with draft overrides applied) */
  spec: ResolvedDealCardSpecV2 | null;
  /** The template override from editor state */
  templateOverride?: Record<string, unknown> | null;
  /** Deal type key */
  dealTypeKey?: string;
  /** Template labels from template_json */
  templateLabels?: Record<string, string> | null;
  /** Override labels from overrides_json */
  overridesLabels?: Record<string, string> | null;
  /** Draft labels (unsaved) */
  draftLabels?: Record<string, string> | null;
  /** CR316: Collected render statuses from blocks */
  renderStatuses?: RenderStatusCollection | null;
  /** CR315: Raw template data for theme-owned key detection */
  rawTemplateJson?: Record<string, unknown> | null;
  rawLegacyTemplateJson?: Record<string, unknown> | null;
  rawOverridesJson?: Record<string, unknown> | null;
  /** Whether the panel is initially open */
  defaultOpen?: boolean;
  className?: string;
}

// CR310: Get badge color based on label source
function getLabelSourceBadgeClass(source: LabelSource): string {
  switch (source) {
    case "draft":
      return "bg-amber-500/20 text-amber-700 border-amber-300";
    case "overrides":
      return "bg-green-500/20 text-green-700 border-green-300";
    case "template":
      return "bg-blue-500/20 text-blue-700 border-blue-300";
    case "legacy_bridge":
      return "bg-purple-500/20 text-purple-700 border-purple-300";
    default:
      return "bg-muted text-muted-foreground";
  }
}

export function V2DebugInspector({
  spec,
  templateOverride,
  dealTypeKey,
  templateLabels,
  overridesLabels,
  draftLabels,
  renderStatuses,
  rawTemplateJson,
  rawLegacyTemplateJson,
  rawOverridesJson,
  defaultOpen = false,
  className,
}: V2DebugInspectorProps) {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  const [showRawJson, setShowRawJson] = useState(false);
  const [showLabelsJson, setShowLabelsJson] = useState(false);
  const [showRenderStatus, setShowRenderStatus] = useState(true);
  
  // CR307: Compute unknown toggle IDs (must be before early returns)
  const unknownToggles = useMemo(() => {
    if (!spec) return { primary: [], details: [] };
    
    const surfaceOverrides = templateOverride?.surfaceOverrides as { 
      primary?: { blockToggles?: Record<string, boolean> }; 
      details?: { blockToggles?: Record<string, boolean> };
    } | undefined;
    
    const unknownPrimary = getUnknownToggleIds(
      surfaceOverrides?.primary?.blockToggles,
      REGISTRY_PRIMARY_BLOCK_IDS
    );
    const unknownDetails = getUnknownToggleIds(
      surfaceOverrides?.details?.blockToggles,
      REGISTRY_DETAILS_BLOCK_IDS
    );
    
    return { primary: unknownPrimary, details: unknownDetails };
  }, [spec, templateOverride]);
  
  // CR310: Resolve labels with source tracking
  const labelsResult = useMemo(() => {
    const effectiveDealType = dealTypeKey || spec?.resolved?.dealTypeKey || "UNCLASSIFIED";
    return resolveEffectiveLabels({
      dealType: effectiveDealType,
      templateLabels: templateLabels || undefined,
      overridesLabels: overridesLabels || (spec?.resolved?.labels as Record<string, string>) || undefined,
      draftLabels: draftLabels || undefined,
    });
  }, [dealTypeKey, spec, templateLabels, overridesLabels, draftLabels]);
  
  // CR310: Get universal block IDs for registry display
  const universalBlockIds = useMemo(() => ({
    primary: getUniversalPrimaryBlockIds(),
    details: getUniversalDetailsBlockIds(),
  }), []);
  
  // CR315: Check for missing universal blocks in registry
  const missingBlocks = useMemo(() => {
    if (!spec) return { primary: [], details: [] };
    
    const primaryBlockIds = spec.surfaces.primary?.blocks.map(b => b.id) || [];
    const detailsBlockIds = spec.surfaces.details?.blocks.map(b => b.id) || [];
    
    return {
      primary: getMissingUniversalBlocks(primaryBlockIds, "primary"),
      details: getMissingUniversalBlocks(detailsBlockIds, "details"),
    };
  }, [spec]);
  
  const hasMissingBlocks = missingBlocks.primary.length > 0 || missingBlocks.details.length > 0;
  
  // CR315: Analyze for theme-owned keys presence
  const themeKeysAnalysis = useMemo(() => {
    return analyzeTemplateForThemeKeys(
      rawTemplateJson,
      rawLegacyTemplateJson,
      rawOverridesJson
    );
  }, [rawTemplateJson, rawLegacyTemplateJson, rawOverridesJson]);
  
  // CR312: Extract effective block toggles from surfaceOverrides (SSoT for V2)
  // Must be before early return as it's a hook
  const effectiveBlockToggles = useMemo(() => {
    if (!spec) return { ribbon: true, details_cta: true };
    
    const primaryBlocks = spec.surfaces.primary?.blocks || [];
    const ribbonBlock = primaryBlocks.find(b => b.id === "ribbon");
    const detailsCtaBlock = primaryBlocks.find(b => b.id === "details_cta");
    
    return {
      ribbon: ribbonBlock?.enabled ?? true,
      details_cta: detailsCtaBlock?.enabled ?? true,
    };
  }, [spec]);
  
  // Early return after all hooks
  if (!spec) {
    return (
      <div className={cn("p-2 text-xs text-muted-foreground bg-muted/30 rounded border", className)}>
        <Bug className="h-3 w-3 inline mr-1" />
        V2 Spec not loaded
      </div>
    );
  }
  
  const { resolved, surfaces } = spec;
  const styleOverrides = resolved.styleOverrides || {};
  
  // CR312: Extract key visibility toggles - remove showRibbon for V2 (it's legacy/ignored)
  // For V2, ribbon visibility is controlled solely by blockToggles.ribbon
  const visibilityToggles = {
    showMerchantLogo: styleOverrides.showMerchantLogo,
    showMerchantName: styleOverrides.showMerchantName,
    // CR312: showRibbon removed - V2 uses blockToggles.ribbon only
    showStarburst: styleOverrides.showStarburst,
  };
  
  // Get enabled/disabled blocks for primary surface
  const primaryBlocks = surfaces.primary?.blocks || [];
  const enabledPrimaryBlocks = primaryBlocks.filter(b => b.enabled);
  const disabledPrimaryBlocks = primaryBlocks.filter(b => !b.enabled);
  
  // Get enabled/disabled blocks for details surface
  const detailsBlocks = surfaces.details?.blocks || [];
  const enabledDetailsBlocks = detailsBlocks.filter(b => b.enabled);
  const disabledDetailsBlocks = detailsBlocks.filter(b => !b.enabled);
  
  const hasUnknownToggles = unknownToggles.primary.length > 0 || unknownToggles.details.length > 0;
  
  // Get draft override indicators
  const hasDraftLabels = templateOverride?.labels && Object.keys(templateOverride.labels as object).length > 0;
  const hasDraftStyling = templateOverride?.styling && Object.keys(templateOverride.styling as object).length > 0;
  const hasDraftSections = (templateOverride?.sections || templateOverride?.extended_sections);
  const hasDraftSurfaceOverrides = templateOverride?.surfaceOverrides;
  
  // CR316: Render status helpers
  const getRenderStatusIcon = (rendered: boolean, reason: string) => {
    if (rendered) return <Check className="h-2.5 w-2.5 text-green-600" />;
    if (reason === "toggle_off") return <EyeOff className="h-2.5 w-2.5 text-muted-foreground" />;
    if (reason === "no_data") return <HelpCircle className="h-2.5 w-2.5 text-amber-500" />;
    if (reason === "error") return <X className="h-2.5 w-2.5 text-destructive" />;
    return <HelpCircle className="h-2.5 w-2.5 text-muted-foreground" />;
  };
  
  return (
    <Collapsible 
      open={isOpen} 
      onOpenChange={setIsOpen}
      className={cn("border rounded-lg bg-muted/20", className)}
    >
      <CollapsibleTrigger asChild>
        <Button 
          variant="ghost" 
          size="sm" 
          className="w-full h-8 px-2 justify-between text-[11px] hover:bg-muted/50"
        >
          <div className="flex items-center gap-1.5">
            <Bug className="h-3 w-3 text-purple-500" />
            <span className="font-medium">V2 Debug Inspector</span>
            <Badge variant="outline" className="h-4 text-[9px] px-1 ml-1">
              {resolved.dealTypeKey}
            </Badge>
            {hasMissingBlocks && (
              <Badge variant="destructive" className="h-4 text-[8px] px-1">
                ⚠ Missing Blocks
              </Badge>
            )}
            {hasUnknownToggles && (
              <Badge variant="destructive" className="h-4 text-[8px] px-1">
                ⚠ Unknown IDs
              </Badge>
            )}
            {themeKeysAnalysis.themeKeysPresent && (
              <Badge variant="destructive" className="h-4 text-[8px] px-1">
                ⚠ Theme Keys
              </Badge>
            )}
            {!themeKeysAnalysis.themeKeysPresent && (
              <Badge variant="outline" className="h-4 text-[8px] px-1 bg-green-500/10 text-green-700 border-green-300">
                <Shield className="h-2.5 w-2.5 mr-0.5" />
                Clean
              </Badge>
            )}
          </div>
          <ChevronDown className={cn("h-3 w-3 transition-transform", isOpen && "rotate-180")} />
        </Button>
      </CollapsibleTrigger>
      
      <CollapsibleContent>
        <ScrollArea className="max-h-[500px]">
          <div className="p-2 space-y-3 text-[10px]">
            {/* Spec Identification */}
            <div className="space-y-1">
              <div className="flex items-center gap-1 text-muted-foreground font-medium">
                <Code2 className="h-3 w-3" />
                Spec Identity
              </div>
              <div className="grid grid-cols-2 gap-1 pl-4">
                <span className="text-muted-foreground">Deal Type:</span>
                <span className="font-mono">{resolved.dealTypeKey}</span>
                <span className="text-muted-foreground">Variant:</span>
                <span className="font-mono">{resolved.variantKey}</span>
                <span className="text-muted-foreground">Renderer:</span>
                <Badge variant="default" className="h-4 text-[9px] px-1 w-fit">V2</Badge>
                <span className="text-muted-foreground">Sources:</span>
                <span className="font-mono text-[9px]">
                  v:{resolved.sources?.variant} / o:{resolved.sources?.overrides}
                </span>
                {/* CR314: Structure driver indicator */}
                <span className="text-muted-foreground">Structure Driver:</span>
                <Badge variant="outline" className="h-4 text-[8px] px-1 w-fit bg-green-500/10 text-green-700 border-green-300">
                  v2_registry_blocks
                </Badge>
              </div>
            </div>
            
            {/* CR315: Theme-Owned Keys Status */}
            <div className="space-y-1">
              <div className="flex items-center gap-1 text-muted-foreground font-medium">
                <Shield className="h-3 w-3" />
                Theme Keys Guard (CR315)
              </div>
              <div className="pl-4 space-y-0.5">
                <div className="flex items-center gap-2">
                  <span className="text-muted-foreground">Status:</span>
                  {themeKeysAnalysis.themeKeysPresent ? (
                    <Badge variant="destructive" className="h-4 text-[8px] px-1">
                      ⚠ Found in DB
                    </Badge>
                  ) : (
                    <Badge variant="outline" className="h-4 text-[8px] px-1 bg-green-500/10 text-green-700 border-green-300">
                      ✓ Clean
                    </Badge>
                  )}
                </div>
                {themeKeysAnalysis.themeKeysPresent && (
                  <>
                    {themeKeysAnalysis.templateJsonKeys.length > 0 && (
                      <div className="flex gap-2 text-[9px]">
                        <span className="text-destructive">template_json:</span>
                        <span className="font-mono">{themeKeysAnalysis.templateJsonKeys.join(", ")}</span>
                      </div>
                    )}
                    {themeKeysAnalysis.legacyTemplateJsonKeys.length > 0 && (
                      <div className="flex gap-2 text-[9px]">
                        <span className="text-destructive">legacy_template_json:</span>
                        <span className="font-mono">{themeKeysAnalysis.legacyTemplateJsonKeys.join(", ")}</span>
                      </div>
                    )}
                    {themeKeysAnalysis.overridesJsonKeys.length > 0 && (
                      <div className="flex gap-2 text-[9px]">
                        <span className="text-destructive">overrides_json:</span>
                        <span className="font-mono">{themeKeysAnalysis.overridesJsonKeys.join(", ")}</span>
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
            {/* CR316: Render Status Section */}
            {renderStatuses && (renderStatuses.primary.length > 0 || renderStatuses.details.length > 0) && (
              <div className="space-y-1">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowRenderStatus(!showRenderStatus)}
                  className="h-6 text-[10px] px-1 w-full justify-start text-muted-foreground font-medium"
                >
                  {showRenderStatus ? <ChevronDown className="h-2.5 w-2.5 mr-1" /> : <ChevronRight className="h-2.5 w-2.5 mr-1" />}
                  <Layers className="h-3 w-3 mr-1" />
                  Render Status (CR316)
                  <Badge variant="outline" className="h-4 text-[8px] px-1 ml-2">
                    {renderStatuses.primary.filter(s => s.rendered).length + renderStatuses.details.filter(s => s.rendered).length} rendered
                  </Badge>
                </Button>
                
                {showRenderStatus && (
                  <div className="pl-4 space-y-2">
                    {/* Primary Surface */}
                    {renderStatuses.primary.length > 0 && (
                      <div className="space-y-1">
                        <div className="text-[9px] font-medium text-muted-foreground">Primary Surface</div>
                        <div className="space-y-0.5">
                          {renderStatuses.primary.map(status => (
                            <div key={status.blockId} className="flex items-center gap-2 text-[9px]">
                              {getRenderStatusIcon(status.rendered, status.reason)}
                              <span className="font-mono min-w-[100px]">{status.blockId}</span>
                              <Badge 
                                variant="outline" 
                                className={cn("h-4 text-[7px] px-1", getRenderReasonBadgeClass(status.reason))}
                              >
                                {getRenderReasonDisplayText(status.reason)}
                              </Badge>
                              <span className="text-muted-foreground">
                                enabled={String(status.effectiveEnabled)}
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    
                    {/* Details Surface */}
                    {renderStatuses.details.length > 0 && (
                      <div className="space-y-1">
                        <div className="text-[9px] font-medium text-muted-foreground">Details Surface</div>
                        <div className="space-y-0.5">
                          {renderStatuses.details.map(status => (
                            <div key={status.blockId} className="flex items-center gap-2 text-[9px]">
                              {getRenderStatusIcon(status.rendered, status.reason)}
                              <span className="font-mono min-w-[100px]">{status.blockId}</span>
                              <Badge 
                                variant="outline" 
                                className={cn("h-4 text-[7px] px-1", getRenderReasonBadgeClass(status.reason))}
                              >
                                {getRenderReasonDisplayText(status.reason)}
                              </Badge>
                              <span className="text-muted-foreground">
                                enabled={String(status.effectiveEnabled)}
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
            
            {/* Visibility Toggles (Legacy styleOverrides - for reference only) */}
            <div className="space-y-1">
              <div className="flex items-center gap-1 text-muted-foreground font-medium">
                <Eye className="h-3 w-3" />
                Visibility (styleOverrides)
                {hasDraftStyling && (
                  <Badge variant="secondary" className="h-4 text-[8px] px-1 bg-amber-500/20 text-amber-700">
                    DRAFT
                  </Badge>
                )}
                <Badge variant="outline" className="h-4 text-[7px] px-1 ml-1 text-muted-foreground">
                  legacy
                </Badge>
              </div>
              <div className="grid grid-cols-2 gap-1 pl-4">
                {Object.entries(visibilityToggles).map(([key, value]) => (
                  <div key={key} className="contents">
                    <span className="text-muted-foreground">{key}:</span>
                    <span className="flex items-center gap-1">
                      {value === true && <Eye className="h-2.5 w-2.5 text-green-600" />}
                      {value === false && <EyeOff className="h-2.5 w-2.5 text-destructive" />}
                      <span className={cn(
                        "font-mono",
                        value === true && "text-green-600",
                        value === false && "text-destructive",
                        value === undefined && "text-muted-foreground"
                      )}>
                        {value === undefined ? "—" : String(value)}
                      </span>
                    </span>
                  </div>
                ))}
              </div>
              <p className="text-[8px] text-muted-foreground/70 pl-4 italic">
                Note: For V2, ribbon visibility is controlled by blockToggles.ribbon (see below)
              </p>
            </div>
            
            {/* CR312: Effective Block Toggles (SSoT for V2) */}
            <div className="space-y-1 bg-primary/5 p-2 rounded border border-primary/20">
              <div className="flex items-center gap-1 text-primary font-medium">
                <Layers className="h-3 w-3" />
                Effective Block Toggles (CR312)
                <Badge variant="outline" className="h-4 text-[7px] px-1 bg-green-500/10 text-green-700 border-green-300">
                  SSoT
                </Badge>
              </div>
              <div className="grid grid-cols-2 gap-1 pl-4">
                <span className="text-muted-foreground">blockToggles.ribbon:</span>
                <span className="flex items-center gap-1">
                  {effectiveBlockToggles.ribbon ? (
                    <Eye className="h-2.5 w-2.5 text-green-600" />
                  ) : (
                    <EyeOff className="h-2.5 w-2.5 text-destructive" />
                  )}
                  <span className={cn(
                    "font-mono",
                    effectiveBlockToggles.ribbon ? "text-green-600" : "text-destructive"
                  )}>
                    {String(effectiveBlockToggles.ribbon)}
                  </span>
                </span>
                
                <span className="text-muted-foreground">blockToggles.details_cta:</span>
                <span className="flex items-center gap-1">
                  {effectiveBlockToggles.details_cta ? (
                    <Eye className="h-2.5 w-2.5 text-green-600" />
                  ) : (
                    <EyeOff className="h-2.5 w-2.5 text-destructive" />
                  )}
                  <span className={cn(
                    "font-mono",
                    effectiveBlockToggles.details_cta ? "text-green-600" : "text-destructive"
                  )}>
                    {String(effectiveBlockToggles.details_cta)}
                  </span>
                </span>
              </div>
              <p className="text-[8px] text-muted-foreground/70 pl-4">
                These are the actual values controlling visibility in V2 rendering.
              </p>
            </div>
            
            {/* Primary Surface Block Toggles */}
            <div className="space-y-1">
              <div className="flex items-center gap-1 text-muted-foreground font-medium">
                <Layers className="h-3 w-3" />
                Primary Surface Blocks
                {(hasDraftSections || hasDraftSurfaceOverrides) && (
                  <Badge variant="secondary" className="h-4 text-[8px] px-1 bg-amber-500/20 text-amber-700">
                    DRAFT
                  </Badge>
                )}
              </div>
              <div className="pl-4 space-y-1">
                <div>
                  <span className="text-green-600 font-medium">Enabled ({enabledPrimaryBlocks.length}): </span>
                  <span className="font-mono text-[9px]">
                    {enabledPrimaryBlocks.map(b => b.id).join(", ") || "—"}
                  </span>
                </div>
                <div>
                  <span className="text-destructive font-medium">Disabled ({disabledPrimaryBlocks.length}): </span>
                  <span className="font-mono text-[9px]">
                    {disabledPrimaryBlocks.map(b => b.id).join(", ") || "—"}
                  </span>
                </div>
              </div>
            </div>
            
            {/* Details Surface Block Toggles */}
            <div className="space-y-1">
              <div className="flex items-center gap-1 text-muted-foreground font-medium">
                <LayoutList className="h-3 w-3" />
                Details Surface Blocks
              </div>
              <div className="pl-4 space-y-1">
                <div>
                  <span className="text-green-600 font-medium">Enabled ({enabledDetailsBlocks.length}): </span>
                  <span className="font-mono text-[9px]">
                    {enabledDetailsBlocks.map(b => b.id).join(", ") || "—"}
                  </span>
                </div>
                <div>
                  <span className="text-destructive font-medium">Disabled ({disabledDetailsBlocks.length}): </span>
                  <span className="font-mono text-[9px]">
                    {disabledDetailsBlocks.map(b => b.id).join(", ") || "—"}
                  </span>
                </div>
              </div>
            </div>
            
            {/* CR310: Registry Block IDs (Universal) */}
            <div className="space-y-1">
              <div className="flex items-center gap-1 text-muted-foreground font-medium">
                <Tag className="h-3 w-3" />
                Universal Block IDs (CR310)
              </div>
              <div className="pl-4 space-y-1 font-mono text-[8px]">
                <div>
                  <span className="text-muted-foreground">Primary: </span>
                  <span>{universalBlockIds.primary.join(", ")}</span>
                </div>
                <div>
                  <span className="text-muted-foreground">Details: </span>
                  <span>{universalBlockIds.details.join(", ")}</span>
                </div>
              </div>
            </div>
            
            {/* CR315: Missing Universal Blocks Warning */}
            {hasMissingBlocks && (
              <div className="space-y-1 bg-destructive/10 p-2 rounded border border-destructive/30">
                <div className="flex items-center gap-1 text-destructive font-medium">
                  <AlertTriangle className="h-3 w-3" />
                  Missing Universal Blocks (CR315)
                </div>
                <div className="pl-4 space-y-1 font-mono text-[9px]">
                  {missingBlocks.primary.length > 0 && (
                    <div>
                      <span className="text-muted-foreground">Primary: </span>
                      <span className="text-destructive">{missingBlocks.primary.join(", ")}</span>
                    </div>
                  )}
                  {missingBlocks.details.length > 0 && (
                    <div>
                      <span className="text-muted-foreground">Details: </span>
                      <span className="text-destructive">{missingBlocks.details.join(", ")}</span>
                    </div>
                  )}
                </div>
                <p className="text-[8px] text-destructive/80 mt-1">
                  These blocks must be added to variant registry for true consistency.
                </p>
              </div>
            )}
            
            {/* Compliant Status Badge */}
            {!hasMissingBlocks && (
              <div className="flex items-center gap-2 p-2 bg-green-500/10 rounded border border-green-300">
                <Badge variant="outline" className="h-5 text-[9px] px-2 bg-green-500/20 text-green-700 border-green-300">
                  ✓ Universal Block Contract: COMPLIANT
                </Badge>
                <span className="text-[9px] text-muted-foreground">missingUniversalBlocks: []</span>
              </div>
            )}
            
            {/* Unknown Toggle IDs Warning */}
            {hasUnknownToggles && (
              <div className="space-y-1 bg-destructive/10 p-2 rounded border border-destructive/30">
                <div className="flex items-center gap-1 text-destructive font-medium">
                  <AlertTriangle className="h-3 w-3" />
                  Unknown Toggle IDs (not in registry)
                </div>
                <div className="pl-4 space-y-1 font-mono text-[9px]">
                  {unknownToggles.primary.length > 0 && (
                    <div>
                      <span className="text-muted-foreground">Primary: </span>
                      <span className="text-destructive">{unknownToggles.primary.join(", ")}</span>
                    </div>
                  )}
                  {unknownToggles.details.length > 0 && (
                    <div>
                      <span className="text-muted-foreground">Details: </span>
                      <span className="text-destructive">{unknownToggles.details.join(", ")}</span>
                    </div>
                  )}
                </div>
                <p className="text-[8px] text-destructive/80 mt-1">
                  These toggle IDs won't affect rendering. Update registry or fix toggle IDs.
                </p>
              </div>
            )}
            
            {/* CR310: Labels with Source Tracing */}
            <div className="space-y-1">
              <div className="flex items-center gap-1 text-muted-foreground font-medium">
                <Palette className="h-3 w-3" />
                Effective Labels (CR310)
                {hasDraftLabels && (
                  <Badge variant="secondary" className="h-4 text-[8px] px-1 bg-amber-500/20 text-amber-700">
                    DRAFT
                  </Badge>
                )}
              </div>
              <div className="pl-4 space-y-1">
                {Object.entries(labelsResult.labels).map(([key, value]) => {
                  const source = labelsResult.sources[key] || "default";
                  return (
                    <div key={key} className="flex items-center gap-2">
                      <span className="text-muted-foreground font-mono text-[9px] min-w-[140px] truncate">{key}:</span>
                      <span className="font-mono text-[9px] flex-1 truncate" title={value}>"{value}"</span>
                      <Badge 
                        variant="outline" 
                        className={cn("h-4 text-[7px] px-1 shrink-0", getLabelSourceBadgeClass(source))}
                      >
                        {getLabelSourceDisplayName(source)}
                      </Badge>
                    </div>
                  );
                })}
              </div>
              
              {/* Toggle Labels JSON */}
              <div className="pl-4 pt-1">
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={() => setShowLabelsJson(!showLabelsJson)}
                  className="h-5 text-[8px] px-1 -ml-1"
                >
                  {showLabelsJson ? <ChevronDown className="h-2 w-2 mr-0.5" /> : <ChevronRight className="h-2 w-2 mr-0.5" />}
                  Raw Labels JSON
                </Button>
                {showLabelsJson && (
                  <pre className="mt-1 p-1.5 bg-background/80 rounded text-[8px] font-mono overflow-auto max-h-24">
                    {JSON.stringify({ labels: labelsResult.labels, sources: labelsResult.sources }, null, 2)}
                  </pre>
                )}
              </div>
            </div>
            
            {/* Raw styleOverrides JSON Toggle */}
            <div className="pt-1 border-t">
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={() => setShowRawJson(!showRawJson)}
                className="h-6 text-[9px] px-2 w-full justify-start"
              >
                {showRawJson ? <ChevronDown className="h-2.5 w-2.5 mr-1" /> : <ChevronRight className="h-2.5 w-2.5 mr-1" />}
                {showRawJson ? "Hide" : "Show"} Raw styleOverrides JSON
              </Button>
              {showRawJson && (
                <pre className="mt-1 p-2 bg-background/80 rounded text-[8px] font-mono overflow-auto max-h-32">
                  {JSON.stringify(styleOverrides, null, 2)}
                </pre>
              )}
            </div>
          </div>
        </ScrollArea>
      </CollapsibleContent>
    </Collapsible>
  );
}

export default V2DebugInspector;
